import { useEffect, useState } from 'react';
import { useTheme } from '@mui/material';
import Autocomplete from '@/components/ui/Autocomplete/Autocomplete';
import Box from '@/components/ui/Box/Box';
import Button from '@/components/ui/Button/Button';
import Card from '@/components/ui/Card/Card';
import FieldWrapper from '@/components/ui/FieldWrapper/FieldWrapper';
import Paper from '@/components/ui/Paper/Paper';
import TextField from '@/components/ui/TextField/TextField';
import Typography from '@/components/ui/Typography/Typography';
import VehicleColorDot from '@/components/business/VehicleColorDot/VehicleColorDot';
import { locationsApi } from '@/api/locations';
import { useCompany } from '@/providers/CompanyProvider/CompanyProvider';
import { useLocations } from '@/providers/LocationsProvider/LocationsProvider';
import { useNewJob } from '@/providers/NewJobProvider/NewJobProvider';
import { useRepairers } from '@/providers/RepairersProvider/RepairersProvider';
import { VEHICLE_COLORS } from '@/helpers/vehicleOptions';
import type { Location, Repairer } from '@/types/types';
import type { NewJobFormProps } from './INewJobForm';
import { styles } from './styles';

const NewJobForm = ({ onAddService }: NewJobFormProps) => {
    const theme = useTheme();
    const newJob = useNewJob();
    const locationsCtx = useLocations();
    const repairersCtx = useRepairers();
    const companyCtx = useCompany();

    const isRepairer = companyCtx.state.company?.marketplace_side === 'Repairer';
    const [locationOptions, setLocationOptions] = useState<Location[]>([]);

    useEffect(() => {
        void locationsCtx.actions.refreshLocations();
        if (isRepairer) {
            void repairersCtx.actions.refreshRepairers();
        }
    }, [isRepairer]);

    useEffect(() => {
        if (!isRepairer) {
            setLocationOptions(locationsCtx.state.locations);
        }
    }, [isRepairer, locationsCtx.state.locations]);

    // Repairer-side: when a Supplier is picked, load that supplier's
    // locations. Without this the Location autocomplete stays empty.
    const supplierId = newJob.state.supplier?.id;
    useEffect(() => {
        if (!isRepairer) return;
        if (!supplierId) {
            setLocationOptions([]);
            return;
        }
        let cancelled = false;
        locationsApi
            .byCompany(supplierId)
            .then((res) => {
                if (!cancelled) setLocationOptions(res.data?.payload ?? []);
            })
            .catch(() => {
                if (!cancelled) setLocationOptions([]);
            });
        return () => {
            cancelled = true;
        };
    }, [isRepairer, supplierId]);

    const supplierOptions: Repairer[] = repairersCtx.state.repairers;

    return (
        <Paper sx={styles.paper}>
            <Box sx={styles.header}>
                <Typography sx={styles.title}>New Job Request</Typography>
            </Box>
            <Card sx={styles.innerCard}>
                {isRepairer && (
                    <FieldWrapper filled={Boolean(newJob.state.supplier)} required>
                        <Autocomplete<Repairer>
                            options={supplierOptions}
                            getOptionLabel={(o) => o.name ?? ''}
                            value={newJob.state.supplier ?? null}
                            onChange={(_, value) => {
                                newJob.DISPATCH({ type: 'SET_SUPPLIER', payload: value });
                                newJob.DISPATCH({ type: 'SET_LOCATION', payload: null });
                                setLocationOptions([]);
                            }}
                            label="Supplier"
                            required
                        />
                    </FieldWrapper>
                )}
                <FieldWrapper filled={Boolean(newJob.state.location)} required>
                    <Autocomplete<Location>
                        options={locationOptions}
                        getOptionLabel={(o) => o.nickname ?? o.formatted_address ?? 'Location'}
                        value={newJob.state.location}
                        onChange={(_, value) =>
                            newJob.DISPATCH({ type: 'SET_LOCATION', payload: value })
                        }
                        label="Location"
                        required
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(newJob.state.vin)} required>
                    <TextField
                        fullWidth
                        label="VIN"
                        value={newJob.state.vin}
                        required
                        inputProps={{ style: { textTransform: 'uppercase' } }}
                        onChange={(e) =>
                            newJob.DISPATCH({ type: 'SET_VIN', payload: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(newJob.state.color)} required>
                    <Autocomplete<string>
                        options={VEHICLE_COLORS}
                        value={newJob.state.color || null}
                        onChange={(_, value) =>
                            newJob.DISPATCH({ type: 'SET_COLOR', payload: value ?? '' })
                        }
                        label="Color"
                        required
                        renderOption={(props, option) => {
                            const { key, ...rest } = props as typeof props & {
                                key?: React.Key;
                            };
                            return (
                                <li key={key} {...rest}>
                                    <Box
                                        component="span"
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                        }}
                                    >
                                        <VehicleColorDot color={option} />
                                        <Box component="span">{option}</Box>
                                    </Box>
                                </li>
                            );
                        }}
                        startAdornment={
                            newJob.state.color ? (
                                <VehicleColorDot color={newJob.state.color} />
                            ) : null
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(newJob.state.odometer)}>
                    <TextField
                        fullWidth
                        label="Odometer"
                        value={newJob.state.odometer}
                        type="number"
                        onChange={(e) =>
                            newJob.DISPATCH({ type: 'SET_ODO', payload: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(newJob.state.stockRO)}>
                    <TextField
                        fullWidth
                        label="Stock/RO"
                        value={newJob.state.stockRO}
                        onChange={(e) =>
                            newJob.DISPATCH({ type: 'SET_STOCK', payload: e.target.value })
                        }
                    />
                </FieldWrapper>
                <FieldWrapper filled={Boolean(newJob.state.poNumber)}>
                    <TextField
                        fullWidth
                        label="PO Number"
                        value={newJob.state.poNumber}
                        onChange={(e) =>
                            newJob.DISPATCH({ type: 'SET_PO', payload: e.target.value })
                        }
                    />
                </FieldWrapper>
            </Card>
            <Box sx={styles.addServiceRow}>
                <Button
                    variant="outlined"
                    sx={styles.addServiceBtn(theme)}
                    onClick={onAddService}
                >
                    Add Service
                </Button>
            </Box>
        </Paper>
    );
};

export default NewJobForm;
